import { useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SearchIcon from "@mui/icons-material/Search";
import { getAutocompleteSuggestions } from "airport-data-js";
import { AIRPORTS } from "../constants";
import "./BookFlight.css";

const AVIASALES_BASE = "https://book.renuairhub.com";

// Build Aviasales deep link with search params
const buildAviasalesUrl = (from, to, departDate, returnDate, passengers) => {
  // Extract IATA codes from "Airport Name (XXX)" format
  const fromMatch = from.match(/\(([A-Z]{3})\)/);
  const toMatch = to.match(/\(([A-Z]{3})\)/);
  if (!fromMatch || !toMatch) return AVIASALES_BASE;

  const origin = fromMatch[1];
  const dest = toMatch[1];

  // Format dates: YYYY-MM-DD → DDMM
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [, month, day] = dateStr.split("-");
    return `${day}${month}`;
  };

  const depart = formatDate(departDate);
  const ret = formatDate(returnDate);

  // Aviasales passenger format: adults, children (2-12), infants (0-2)
  const adults = passengers ? passengers.adults : 1;
  const children = passengers ? passengers.children : 0;
  const infants = passengers ? passengers.infants : 0;

  // Aviasales URL format: /search/ORIGDDMMDESTDDMMADULTSCHILD_AGESINFANTS
  // Passenger segment: {adults}{children}{infants} e.g., "210" = 2 adults, 1 child, 0 infants
  const searchPath = `${origin}${depart}${dest}${ret}${adults}${children > 0 || infants > 0 ? children.toString() + infants.toString() : ""}`;

  return `https://book.renuairhub.com/search/${searchPath}`;
};

function BookFlight({ isOpen, onClose }) {
  const { t, i18n } = useTranslation();
  const [tripType, setTripType] = useState("roundtrip");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [passengers, setPassengers] = useState({
    adults: 1,
    children: 0,
    infants: 0,
  });
  const [showFromSuggestions, setShowFromSuggestions] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showToSuggestions, setShowToSuggestions] = useState(false);
  const [error, setError] = useState("");
  const [activeField, setActiveField] = useState("from");

  const [fromSuggestions, setFromSuggestions] = useState([]);
  const [toSuggestions, setToSuggestions] = useState([]);
  const fromTimer = useRef(null);
  const toTimer = useRef(null);

  const today = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  }, []);

  const fetchSuggestions = useCallback((query, setSuggestions) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    // Check if it's an already-selected value
    const isSelected = query.includes("(") && query.includes(")");
    if (isSelected) {
      setSuggestions([]);
      return;
    }
    getAutocompleteSuggestions(query)
      .then((results) => {
        // Filter to scheduled-service airports only, limit to 8
        const filtered = results
          .filter((a) => a.iata && a.scheduled_service === "TRUE")
          .slice(0, 8)
          .map((a) => ({
            iataCode: a.iata,
            name: a.airport,
            city: a.airport
              .replace(/Airport$/i, "")
              .replace(/Int'l$/i, "")
              .trim(),
            isTracked: AIRPORTS.some((h) => h.iataCode === a.iata),
          }));
        setSuggestions(filtered);
      })
      .catch(() => setSuggestions([]));
  }, []);

  const handleFromChange = useCallback(
    (value) => {
      setFrom(value);
      setShowFromSuggestions(true);
      clearTimeout(fromTimer.current);
      fromTimer.current = setTimeout(
        () => fetchSuggestions(value, setFromSuggestions),
        200,
      );
    },
    [fetchSuggestions],
  );

  const handleToChange = useCallback(
    (value) => {
      setTo(value);
      setShowToSuggestions(true);
      clearTimeout(toTimer.current);
      toTimer.current = setTimeout(
        () => fetchSuggestions(value, setToSuggestions),
        200,
      );
    },
    [fetchSuggestions],
  );

  const handleSwap = useCallback(() => {
    setFrom(to);
    setTo(from);
  }, [from, to]);

  const handleSearch = useCallback(() => {
    setError("");
    if (!from || !to) {
      setError(t("bookFlight.errorFields"));
      return;
    }
    if (!departDate) {
      setError(t("bookFlight.errorDate"));
      return;
    }
    if (tripType === "roundtrip" && !returnDate) {
      setError(t("bookFlight.errorReturnDate"));
      return;
    }
    // Show disclaimer on first search only
    if (!localStorage.getItem("rah_disclaimer_accepted")) {
      setShowDisclaimer(true);
      return;
    }
    // Redirect to book.renuairhub.com with search pre-filled
    const url = buildAviasalesUrl(from, to, departDate, returnDate, passengers);
    const locale = i18n.language === "fr" ? "fr" : "en";
    window.location.href = `${url}?locale=${locale}`;
  }, [
    from,
    to,
    departDate,
    returnDate,
    tripType,
    passengers,
    t,
    i18n.language,
  ]);

  const handleAcceptDisclaimer = useCallback(() => {
    localStorage.setItem("rah_disclaimer_accepted", "1");
    setShowDisclaimer(false);
    // Proceed with search
    const url = buildAviasalesUrl(from, to, departDate, returnDate, passengers);
    const locale = i18n.language === "fr" ? "fr" : "en";
    window.location.href = `${url}?locale=${locale}`;
  }, [from, to, departDate, returnDate, passengers, i18n.language]);

  const handleSelectAirport = useCallback((airport, field) => {
    const value = `${airport.name} (${airport.iataCode})`;
    if (field === "from") {
      setFrom(value);
      setShowFromSuggestions(false);
      setFromSuggestions([]);
    } else {
      setTo(value);
      setShowToSuggestions(false);
      setToSuggestions([]);
    }
  }, []);

  const handleClose = useCallback(() => {
    setError("");
    setFrom("");
    setTo("");
    setDepartDate("");
    setReturnDate("");
    setPassengers({ adults: 1, children: 0, infants: 0 });
    setTripType("roundtrip");
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="bookflight-overlay" onClick={handleClose}>
      <div className="bookflight-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="bookflight-close"
          onClick={handleClose}
          aria-label={t("bookFlight.close")}
        >
          <CloseIcon />
        </button>

        {/* Form always shown — search redirects to Aviasales */}
        <div className="bookflight-form">
          <h2 className="bookflight-title">{t("bookFlight.title")}</h2>

          <div className="bookflight-trip-toggle">
            <button
              className={`trip-btn ${tripType === "roundtrip" ? "active" : ""}`}
              onClick={() => setTripType("roundtrip")}
            >
              {t("bookFlight.roundTrip")}
            </button>
            <button
              className={`trip-btn ${tripType === "oneway" ? "active" : ""}`}
              onClick={() => setTripType("oneway")}
            >
              {t("bookFlight.oneWay")}
            </button>
          </div>

          <div className="bookflight-airports">
            <div className="bookflight-field">
              <FlightTakeoffIcon className="field-icon" />
              <input
                type="text"
                placeholder={t("bookFlight.from")}
                value={from}
                onChange={(e) => handleFromChange(e.target.value)}
                onFocus={() => {
                  setActiveField("from");
                  if (from.length >= 2)
                    fetchSuggestions(from, setFromSuggestions);
                  setShowFromSuggestions(true);
                }}
                onBlur={() =>
                  setTimeout(() => setShowFromSuggestions(false), 200)
                }
              />
              {from && <label>{t("bookFlight.fromLabel")}</label>}
              {from && (
                <button
                  className="bookflight-clear"
                  onClick={() => {
                    setFrom("");
                    setFromSuggestions([]);
                  }}
                  aria-label="Clear"
                >
                  ×
                </button>
              )}
              {showFromSuggestions && fromSuggestions.length > 0 && (
                <div className="bookflight-suggestions">
                  {fromSuggestions.map((a) => (
                    <button
                      key={a.iataCode}
                      onMouseDown={() => handleSelectAirport(a, "from")}
                    >
                      <span className="suggestion-code">{a.iataCode}</span>
                      <span>{a.name}</span>
                      {a.isTracked && (
                        <span className="suggestion-hub">HUB</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="bookflight-swap"
              onClick={handleSwap}
              aria-label={t("bookFlight.swap")}
            >
              <SwapHorizIcon />
            </button>

            <div className="bookflight-field">
              <FlightLandIcon className="field-icon" />
              <input
                type="text"
                placeholder={t("bookFlight.to")}
                value={to}
                onChange={(e) => handleToChange(e.target.value)}
                onFocus={() => {
                  setActiveField("to");
                  if (to.length >= 2) fetchSuggestions(to, setToSuggestions);
                  setShowToSuggestions(true);
                }}
                onBlur={() =>
                  setTimeout(() => setShowToSuggestions(false), 200)
                }
              />
              {to && <label>{t("bookFlight.toLabel")}</label>}
              {to && (
                <button
                  className="bookflight-clear"
                  onClick={() => {
                    setTo("");
                    setToSuggestions([]);
                  }}
                  aria-label="Clear"
                >
                  ×
                </button>
              )}
              {showToSuggestions && toSuggestions.length > 0 && (
                <div className="bookflight-suggestions">
                  {toSuggestions.map((a) => (
                    <button
                      key={a.iataCode}
                      onMouseDown={() => handleSelectAirport(a, "to")}
                    >
                      <span className="suggestion-code">{a.iataCode}</span>
                      <span>{a.name}</span>
                      {a.isTracked && (
                        <span className="suggestion-hub">HUB</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="bookflight-hubs">
            <span className="bookflight-hubs-label">
              {t("bookFlight.trackedHubs")}
            </span>
            <div className="bookflight-hub-chips">
              {AIRPORTS.map((a) => (
                <button
                  key={a.iataCode}
                  className="hub-chip"
                  title={`${a.name} — ${a.country}`}
                  onClick={() => {
                    handleSelectAirport(
                      {
                        iataCode: a.iataCode,
                        name: `${a.city.split(",")[0]}`,
                      },
                      activeField,
                    );
                  }}
                >
                  {a.iataCode} · {a.city.split(",")[0]}
                </button>
              ))}
            </div>
          </div>

          <div className="bookflight-dates">
            <div className="bookflight-field">
              <CalendarTodayIcon className="field-icon" />
              <input
                type="date"
                min={today}
                value={departDate}
                onChange={(e) => {
                  if (e.target.value < today) {
                    setError(t("bookFlight.errorPastDate"));
                    setTimeout(() => setError(""), 5000);
                    return;
                  }
                  setError("");
                  setDepartDate(e.target.value);
                  if (returnDate && e.target.value > returnDate) {
                    setReturnDate("");
                  }
                }}
              />
              <label>{t("bookFlight.departure")}</label>
            </div>
            {tripType === "roundtrip" && (
              <div className="bookflight-field">
                <CalendarTodayIcon className="field-icon" />
                <input
                  type="date"
                  min={departDate || today}
                  value={returnDate}
                  onChange={(e) => {
                    if (e.target.value < (departDate || today)) {
                      setError(t("bookFlight.errorReturnBeforeDepart"));
                      setTimeout(() => setError(""), 5000);
                      return;
                    }
                    setError("");
                    setReturnDate(e.target.value);
                  }}
                />
                <label>{t("bookFlight.return")}</label>
              </div>
            )}
          </div>

          <div className="bookflight-passengers">
            <div className="passenger-row">
              <div className="passenger-info">
                <PersonIcon className="field-icon" />
                <div>
                  <span className="passenger-label">
                    {t("bookFlight.adults")}
                  </span>
                  <span className="passenger-age">
                    {t("bookFlight.adultsAge")}
                  </span>
                </div>
              </div>
              <div className="passenger-controls">
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      adults: Math.max(1, p.adults - 1),
                    }))
                  }
                >
                  −
                </button>
                <span>{passengers.adults}</span>
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      adults: Math.min(9, p.adults + 1),
                    }))
                  }
                >
                  +
                </button>
              </div>
            </div>
            <div className="passenger-row">
              <div className="passenger-info">
                <PersonIcon className="field-icon" />
                <div>
                  <span className="passenger-label">
                    {t("bookFlight.children")}
                  </span>
                  <span className="passenger-age">
                    {t("bookFlight.childrenAge")}
                  </span>
                </div>
              </div>
              <div className="passenger-controls">
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      children: Math.max(0, p.children - 1),
                    }))
                  }
                >
                  −
                </button>
                <span>{passengers.children}</span>
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      children: Math.min(6, p.children + 1),
                    }))
                  }
                >
                  +
                </button>
              </div>
            </div>
            <div className="passenger-row">
              <div className="passenger-info">
                <PersonIcon className="field-icon" />
                <div>
                  <span className="passenger-label">
                    {t("bookFlight.infants")}
                  </span>
                  <span className="passenger-age">
                    {t("bookFlight.infantsAge")}
                  </span>
                </div>
              </div>
              <div className="passenger-controls">
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      infants: Math.max(0, p.infants - 1),
                    }))
                  }
                >
                  −
                </button>
                <span>{passengers.infants}</span>
                <button
                  onClick={() =>
                    setPassengers((p) => ({
                      ...p,
                      infants: Math.min(4, p.infants + 1),
                    }))
                  }
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {error && <p className="bookflight-error">{error}</p>}

          <button className="bookflight-search" onClick={handleSearch}>
            <SearchIcon fontSize="small" />
            <span>{t("bookFlight.searchFlights")}</span>
          </button>
          <p className="bookflight-disclaimer-legal">
            {t("bookFlight.widgetDisclaimer")}
          </p>
        </div>

        {showDisclaimer && (
          <div className="bookflight-disclaimer-popup">
            <div className="bookflight-disclaimer-popup-content">
              <button
                className="bookflight-disclaimer-close"
                onClick={() => setShowDisclaimer(false)}
                aria-label="Close"
              >
                <CloseIcon fontSize="small" />
              </button>
              <h3>{t("bookFlight.disclaimerTitle")}</h3>
              <p>{t("bookFlight.widgetDisclaimer")}</p>
              <button
                className="bookflight-disclaimer-accept"
                onClick={handleAcceptDisclaimer}
              >
                {t("bookFlight.disclaimerAccept")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookFlight;
